
import { createAccordionTokens } from '../tokens/components.js';
import { type SemanticTokens, SEMANTIC } from '../tokens/semantic.js';

/**
 * Accordion Component Configuration
 * Collapsible content panels with border, expand/collapse animation
 */
export function createAccordion(s: SemanticTokens) {
  const accordionTokens = createAccordionTokens(s);
  return {
  root: {
    transitionDuration: accordionTokens.base.transitionDuration,
  },
  panel: {
    borderWidth: '0',
    borderColor: 'transparent',
    gap: accordionTokens.base.panelGap,
  },
  header: {
    color: accordionTokens.fg,
    hoverColor: accordionTokens.fg,
    activeColor: accordionTokens.fg,
    padding: accordionTokens.base.padding,
    fontSize: accordionTokens.header.fontSize,
    lineHeight: accordionTokens.header.lineHeight,
    fontWeight: accordionTokens.header.fontWeight,
    gap: accordionTokens.header.gap,
    borderRadius: accordionTokens.base.radius,
    borderColor: accordionTokens.border,
    background: accordionTokens.bg,
    hoverBackground: accordionTokens.bg,
    activeBackground: accordionTokens.bg,
    activeHoverBackground: accordionTokens.bg,
    focusRing: {
      width: '0',
      style: 'none',
      color: 'transparent',
      offset: '0',
      shadow: 'none',
    },
    toggleIcon: {
      color: accordionTokens.fg,
      hoverColor: accordionTokens.fg,
      activeColor: accordionTokens.fg,
      activeHoverColor: accordionTokens.fg,
      size: accordionTokens.icon.size,
    },
    first: {
      topBorderRadius: accordionTokens.base.radius,
      borderWidth: '1px',
    },
    last: {
      bottomBorderRadius: accordionTokens.base.radius,
      activeBottomBorderRadius: '0',
    },
  },
  content: {
    borderWidth: '0',
    borderColor: 'transparent',
    background: accordionTokens.contentBg,
    color: accordionTokens.contentFg,
    padding: accordionTokens.base.padding,
    fontSize: accordionTokens.content.fontSize,
    fontWeight: accordionTokens.content.fontWeight,
    lineHeight: accordionTokens.content.lineHeight,
  },
  colorScheme: {
    light: {
      header: {
        color: accordionTokens.fg,
        hoverColor: accordionTokens.fg,
        activeColor: accordionTokens.fg,
        background: accordionTokens.bg,
        hoverBackground: accordionTokens.bg,
        activeBackground: accordionTokens.bg,
        activeHoverBackground: accordionTokens.bg,
        toggleIcon: {
          color: accordionTokens.fg,
          hoverColor: accordionTokens.fg,
          activeColor: accordionTokens.fg,
          activeHoverColor: accordionTokens.fg,
        },
      },
      content: {
        background: accordionTokens.contentBg,
        color: accordionTokens.contentFg,
      },
    },
    dark: {
      header: {
        color: accordionTokens.fg,
        hoverColor: accordionTokens.fg,
        activeColor: accordionTokens.fg,
        background: accordionTokens.bg,
        hoverBackground: accordionTokens.bg,
        activeBackground: accordionTokens.bg,
        activeHoverBackground: accordionTokens.bg,
        toggleIcon: {
          color: accordionTokens.fg,
          hoverColor: accordionTokens.fg,
          activeColor: accordionTokens.fg,
          activeHoverColor: accordionTokens.fg,
        },
      },
      content: {
        background: accordionTokens.contentBg,
        color: accordionTokens.contentFg,
      },
    },
  },
  css: () => `
    .p-accordionheader {
      font-size: ${accordionTokens.header.fontSize};
      font-weight: ${accordionTokens.header.fontWeight};
      gap: ${accordionTokens.header.gap};
      background: ${accordionTokens.bg};
      border: 1px solid ${accordionTokens.border};
      border-radius: ${accordionTokens.base.radius};
      margin-bottom: 0;
    }

    .p-accordionheader-toggle-icon {
      font-size: ${accordionTokens.icon.size};
    }

    .p-accordionheader .p-icon {
      width: ${accordionTokens.icon.size};
      height: ${accordionTokens.icon.size};
    }

    .p-accordioncontent-content {
      font-size: ${accordionTokens.content.fontSize};
      font-weight: ${accordionTokens.content.fontWeight};
      line-height: ${accordionTokens.content.lineHeight};
      border-radius: 0 0 ${accordionTokens.base.radius} ${accordionTokens.base.radius};
    }

    .p-accordionheader[aria-expanded="true"] {
      border-radius: ${accordionTokens.base.radius} ${accordionTokens.base.radius} 0 0;
      border-bottom: none !important;
      margin-bottom: 0;
    }

    .p-accordionheader[aria-expanded="true"] + .p-accordioncontent {
      border: 1px solid ${accordionTokens.border};
      border-top: none;
      border-radius: 0 0 ${accordionTokens.base.radius} ${accordionTokens.base.radius};
      margin-bottom: 0;
    }

    .p-accordionheader > .p-iconwrapper > svg {
      width: ${accordionTokens.icon.size};
      height: ${accordionTokens.icon.size};
      transition: transform ${accordionTokens.base.transitionDuration} cubic-bezier(0.4, 0, 0.2, 1);
    }

    .p-accordionheader[aria-expanded="true"] > .p-iconwrapper > svg {
      transform: rotate(180deg);
    }

    .p-accordionpanel {
      margin-bottom: ${accordionTokens.base.panelGap};
    }

    .p-accordionpanel:last-child {
      margin-bottom: 0;
    }

    .p-accordioncontent {
      transition: all ${accordionTokens.base.transitionDuration} cubic-bezier(0.4, 0, 0.2, 1);
    }
  `,
  };
}
export const accordion = createAccordion(SEMANTIC);